import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';

import 'react-toastify/dist/ReactToastify.css';
import './index.css';

import App from './App';
import theme from './theme';
import { store } from './store';
import { ScrollToTop } from './components/ScrollToTop';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// IMPORTANT: Initialize tracking services in correct order BEFORE React app renders
// Initialization order: Consent Mode → Google Ads → GTM
import { initializeConsentMode } from './utils/consentMode';
import gtmService from './utils/gtm';

// Step 1: Initialize Google Consent Mode v2 (sets all to 'denied' by default)
// This ensures consent mode is ready before any tracking tags load
initializeConsentMode();

// Step 2: Initialize tracking services (Google Ads + GTM)
// - Google Ads base tag loads first (required for conversion tracking)
// - GTM loads second (can now use Google Ads for conversions)
// - Both respect consent mode defaults (no cookies until consent granted)
gtmService.initialize().catch((error) => {
  console.error('Failed to initialize tracking services on page load:', error);
});

const root = ReactDOM.createRoot(document.getElementById('root'));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2, // Retry on failure twice
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  }
});

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <Router>
              <QueryClientProvider client={queryClient}>
                <ScrollToTop />
                <App />
                {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
              </QueryClientProvider>
            </Router>
          </ThemeProvider>
        </LocalizationProvider>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);
